import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Activity,
  ArrowRight,
  Check,
  Dumbbell,
  Flame,
  HeartPulse,
  Salad,
  Sparkles,
  Target,
  Zap,
} from "lucide-react";

export default function AnalysisResultPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const form =
    location?.state?.form ||
    safeJsonParse(localStorage.getItem("campaignWizardForm"));

  const analysis =
    location?.state?.analysis ||
    safeJsonParse(localStorage.getItem("campaignAnalysis"));

  const mobileNumber =
    location?.state?.mobileNumber ||
    localStorage.getItem("campaignMobileNumber") ||
    "";

  const planGuid =
    location?.state?.planGuid ||
    localStorage.getItem("campaignPlanGuid") ||
    "";

  const token =
    location?.state?.token ||
    localStorage.getItem("campaignToken") ||
    "";

  useEffect(() => {
    if (!analysis) {
      navigate("/body-analysis/otp");
      return;
    }

    localStorage.setItem("campaignAnalysis", JSON.stringify(analysis));
  }, [analysis, navigate]);

  return (
    <Shell>
      <section className="relative z-10 mx-auto min-h-screen max-w-md px-4 pt-5 pb-28">
        <header className="flex items-center justify-between">
          <img src="/logo.png" alt="MOGYM" className="h-10 w-auto" />

          <div className="rounded-full border border-emerald-400/20 bg-emerald-400/10 px-3 py-2 text-[11px] text-emerald-100 backdrop-blur-2xl">
            تحلیل آماده شد
          </div>
        </header>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-7 text-center"
        >
          <div className="mx-auto grid h-20 w-20 place-items-center rounded-[30px] bg-emerald-300 text-black shadow-[0_0_70px_rgba(52,211,153,.45)]">
            <Sparkles size={36} />
          </div>

          <h1 className="mt-5 text-2xl font-black leading-tight">
            چیزی پیدا کردیم که ممکنه
            <span className="mt-3 block bg-gradient-to-l from-emerald-300 to-cyan-300 bg-clip-text text-transparent">
              دلیل نتیجه نگرفتنت باشه
            </span>
          </h1>

          <p className="mt-4 text-sm leading-7 text-white/62">
            بر اساس اطلاعاتی که وارد کردی، موجیم مسیر کلی بدن و تمرینت رو تحلیل کرده
          </p>
        </motion.div>

        <div className="mt-7 grid grid-cols-2 gap-3">
          <MetricCard icon={Activity} label="BMI" value={analysis?.bmi ?? "-"} />
          <MetricCard icon={Target} label="هدف" value={analysis?.goalTitle ?? "-"} />
          <MetricCard icon={Dumbbell} label="تمرین پیشنهادی" value={analysis?.recommendedTrainingDaysText ?? "-"} />
          <MetricCard icon={Flame} label="زمان تقریبی" value={analysis?.estimatedDurationText ?? "-"} />
        </div>

        <div className="mt-5 rounded-[30px] border border-emerald-400/20 bg-emerald-400/10 p-5 backdrop-blur-2xl">
          <div className="flex items-center gap-2 text-emerald-200">
            <HeartPulse size={20} />
            <h3 className="font-black">جمع‌بندی تحلیل</h3>
          </div>

          <p className="mt-3 text-sm leading-7 text-white/70">
            {analysis?.summary}
          </p>
        </div>

        <div className="mt-5 space-y-3">
          {(analysis?.riskFactors || []).map((item, index) => (
            <InsightCard
              key={index}
              number={index + 1}
              title={item.title}
              desc={item.description}
            />
          ))}
        </div>

        <div className="mt-5 rounded-[30px] border border-cyan-400/20 bg-cyan-400/10 p-5">
          <div className="flex items-center gap-2 text-cyan-200">
            <Salad size={20} />
            <h3 className="font-black">تمرکز پیشنهادی موجیم</h3>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {(analysis?.recommendedFocus || []).map((x) => (
              <span
                key={x}
                className="rounded-full border border-white/10 bg-black/25 px-3 py-2 text-xs text-white/80"
              >
                {x}
              </span>
            ))}
          </div>
        </div>
      </section>

      <div className="fixed bottom-4 left-0 right-0 z-40 px-4">
        <div className="mx-auto max-w-md rounded-[26px] border border-white/10 bg-black/45 p-3 shadow-2xl backdrop-blur-2xl">
          <button
            onClick={() =>
              navigate("/body-analysis/preview", {
                state: {
                  form,
                  analysis,
                  mobileNumber,
                  planGuid,
                  token,
                },
              })
            }
            className="flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-300 via-green-400 to-cyan-400 font-black text-black shadow-[0_0_55px_rgba(52,211,153,.45)]"
          >
            <ArrowRight size={19} />
            مشاهده پیش‌نمایش برنامه من
          </button>
        </div>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <main dir="rtl" className="font-vazir relative min-h-screen overflow-hidden bg-[#030712] text-white">
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(16,185,129,.32),transparent_35%),radial-gradient(circle_at_0%_85%,rgba(37,99,235,.28),transparent_38%),radial-gradient(circle_at_100%_70%,rgba(34,211,238,.16),transparent_30%)]" />
      <div className="fixed inset-0 opacity-[.15] bg-[linear-gradient(rgba(255,255,255,.08)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.08)_1px,transparent_1px)] bg-[size:34px_34px]" />
      <div className="fixed inset-0 bg-gradient-to-b from-transparent via-[#030712]/35 to-[#030712]" />
      {children}
    </main>
  );
}

function MetricCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-[26px] border border-white/10 bg-white/[.055] p-4 shadow-xl backdrop-blur-2xl">
      <div className="grid h-10 w-10 place-items-center rounded-2xl bg-emerald-400/10 text-emerald-300">
        <Icon size={20} />
      </div>
      <div className="mt-4 text-[10px] text-white/42">{label}</div>
      <div className="mt-1 text-lg font-black text-white">{value}</div>
    </div>
  );
}

function InsightCard({ number, title, desc }) {
  return (
    <div className="rounded-[26px] border border-white/10 bg-white/[.05] p-4 backdrop-blur-2xl">
      <div className="flex gap-3">
        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-2xl bg-emerald-300 font-black text-black">
          {number}
        </div>

        <div>
          <div className="font-black">{title}</div>
          <div className="mt-2 text-xs leading-6 text-white/58">{desc}</div>
        </div>
      </div>
    </div>
  );
}

function LoadingStep({ done, text }) {
  return (
    <div
      className={`flex items-center gap-3 rounded-2xl border px-4 py-3 backdrop-blur-2xl ${
        done
          ? "border-emerald-400/20 bg-emerald-400/10"
          : "border-white/10 bg-white/[.045]"
      }`}
    >
      <div
        className={`grid h-7 w-7 place-items-center rounded-full ${
          done ? "bg-emerald-300 text-black" : "bg-white/8 text-white/40"
        }`}
      >
        {done ? <Check size={16} /> : <Zap size={15} />}
      </div>

      <span className="text-sm font-bold text-white/75">{text}</span>
    </div>
  );
}

function safeJsonParse(value) {
  try {
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
}